import React, { useState, useEffect } from 'react';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { ReportsFilterComponent, ReportsFilterComponentProps } from './ReportsFilterComponent';
import { TimeSheetDetailedReport, UserEntityDetails } from '../../axiosApi/models';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { SCOPES } from '../../common/permissions';
import { getUserEntity } from 'common/UserEntityProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import {ENTITY_TYPE, PIECOLORS,ACCESSIBLE_COLORS} from '../../common/constants';
import Spinner from 'components/Spinner';
import { handleAPIError } from '../../common/errorHandler';
import { AttachMoney, Cancel, KeyboardArrowDown, KeyboardArrowUp, Money } from '@mui/icons-material';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { Chart, useChart } from 'components/chart';
import Box from '@mui/material/Box';
import Title from 'components/common/Title';
import { CircularProgress, Stack, Typography, Card } from '@mui/material';
import { HoursByEmployeeReportComponent } from './HoursByEmployeeReportComponent';

interface Task {
    idTask: number;
    taskDescription: string;
    time: number;
    idEmployee: number;
    employeeName: string;
    date: Date
}

interface Job {
    idJob: number;
    jobDescription: string;
    tasks: Task[];
    employees: Employee[];
    billableHours: number;
    totalHours: number;
    nonBillableHours: number;
    billingAmount: number;
}

interface Client {
    idClient: number;
    clientName: string;
    projects: Project[];
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
    billingAmount: number;
}

interface Project {
    idProject: number;
    projectName: string;
    jobs: Job[];
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
    employees: Employee[];
    billingAmount: number;
}

interface Employee {
    idEmployee: number;
    employeeName: string;
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
    monthlyRate: number;
    hourlyRate: number;
    billingAmount: number;
}

type BillingReportComponentProps = {
    client?: boolean;
}

export const BillingReportComponent = (props: BillingReportComponentProps) => {
    const { client } = props;
    const api: Api = useApi();
    const toast = useToastMessageQueue();
    const [results, setResults] = React.useState<TimeSheetDetailedReport[]>([]);
    const [clients, setClients] = React.useState<Client[]>([]);
    const [employeeTotals, setEmployeeTotals] = React.useState<Employee[]>([]);
    const [pieData, setPieData] = React.useState<any>(null);
    const [filterData, setFilterData] = React.useState<any>(null);
    const [loading, setLoading]  = React.useState<Boolean>(true);
    const [errors, setErrors] = useState({});
    const userEntity : UserEntityDetails = getUserEntity();
    
    // Sorting state for clients
    const [clientSortBy, setClientSortBy] = React.useState<keyof Client>('billingAmount');
    const [clientSortOrder, setClientSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const sortedClients = React.useMemo(() => {
        return [...clients].sort((a, b) => {
            let aVal = a[clientSortBy];
            let bVal = b[clientSortBy];
            
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return clientSortOrder === 'asc' 
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }
            
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            return clientSortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        });
    }, [clients, clientSortBy, clientSortOrder]);

    // Currency formatting utility
    const formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const handleFilterChange = (filterData) => {
        fetchData(filterData);
        setFilterData(filterData);
    }
    
    // No need for ChartJS.register since we're using ApexCharts
    
    useEffect(() => {
        // Actualiza el título del documento usando la API del navegador
        const clientJobTaskTree = createClientProjectJobTaskTree(results);
        setClients(clientJobTaskTree);
        
        // Calculate employee totals across all clients and projects
        const employeeTotalsMap = new Map<number, Employee>();
        
        for (const entry of results) {
            let empTotal = employeeTotalsMap.get(entry.idEmployee);
            
            if (!empTotal) {
                empTotal = {
                    idEmployee: entry.idEmployee,
                    employeeName: entry.employeeName,
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time,
                    hourlyRate: entry.hourlyRate,
                    monthlyRate: entry.monthlyRate,
                    billingAmount: !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0)
                };
                employeeTotalsMap.set(entry.idEmployee, empTotal);
            } else {
                empTotal.billableHours += entry.billable ? entry.time : 0;
                empTotal.nonBillableHours += entry.billable ? 0 : entry.time;
                empTotal.totalHours += entry.time;
                empTotal.billingAmount += !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0);
            }
        }
        
        setEmployeeTotals(Array.from(employeeTotalsMap.values()).sort((a, b) => a.employeeName.localeCompare(b.employeeName)));
    }, [results]);


    const fetchData = async (filterData: ReportsFilterComponentProps) => {
        if(client){
            fetchClientData(filterData);
        }else{
            fetchUserData(filterData);
        }
    }


    const fetchClientData = async (filterData: ReportsFilterComponentProps) => {
        setLoading(true);
        const response = await api.reportsApi.apiVversionBillingDetailedReportClientGet("1", filterData.from, filterData.to, filterData.projects, userEntity.entityId, filterData.employees, filterData.jobTypes, {}).then((response) => {
            if (response.data.data) {
                setResults(response.data?.data);
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
        }).finally(() => {
            setLoading(false);
        });
    };

    const fetchUserData = async (filterData: ReportsFilterComponentProps) => {
        setLoading(true);
        const response = await api.reportsApi.apiVversionBillingDetailedReportAllGet("1", filterData.from, filterData.to, filterData.projects, filterData.clients, filterData.employees, filterData.jobTypes, {}).then((response) => {
            if (response.data.data) {
                setResults(response.data?.data);
            };
        }).catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({...errors});
        }).finally(() => {
            setLoading(false);
        });
    };
    
    function createClientProjectJobTaskTree(timesheetData: TimeSheetDetailedReport[]): Client[] {
        const clients: Client[] = [];
        setPieData(null);
        
        for (const entry of timesheetData) {
            let client = clients.find((c) => c.idClient === entry.idClient);

            if (!client) {
                client = {
                    idClient: entry.idClient,
                    clientName: entry.clientName,
                    projects: [],
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time,
                    billingAmount: !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0)
                };
                clients.push(client);
            }
            else {
                client.billableHours += entry.billable ? entry.time : 0;
                client.nonBillableHours += entry.billable ? 0 : entry.time;
                client.totalHours += entry.time;
                client.billingAmount += !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0);
            }


            let project = client.projects.find((p) => p.idProject === entry.idProject);

            if (!project) {
                project = {
                    idProject: entry.idProject,
                    projectName: entry.projectName,
                    jobs: [],
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time,
                    employees: [],
                    billingAmount: !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0)

                };
                client.projects.push(project);
            }
            else {
                project.billableHours += entry.billable ? entry.time : 0;
                project.nonBillableHours += entry.billable ? 0 : entry.time;
                project.totalHours += entry.time;
                project.billingAmount += !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0);
            }

            let job = project.jobs.find((j) => j.idJob === entry.idJob);

            if (!job) {
                job = {
                    idJob: entry.idJob,
                    jobDescription: entry.jobDescription,
                    tasks: [],
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: !entry.billable ? entry.time : 0,
                    totalHours: entry.time,
                    billingAmount: !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0),
                    employees: []
                };
                project.jobs.push(job);
            }
            else {
                job.billableHours += entry.billable ? entry.time : 0;
                job.nonBillableHours += entry.billable ? 0 : entry.time;
                job.totalHours += entry.time;
                job.billingAmount += !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0);
            }


            const task: Task = {
                idTask: entry.idTimesheet,
                taskDescription: entry.taskDescription,
                time: entry.time,
                idEmployee: entry.idEmployee,
                employeeName: entry.employeeName,
                date: entry.date
            };
            job.tasks.push(task);

            let employee = job.employees.find((j) => j.idEmployee === entry.idEmployee);
            if (!employee) {
                employee = {
                    idEmployee: entry.idEmployee,
                    employeeName: entry.employeeName,
                    billableHours: entry.billable ? entry.time : 0,
                    nonBillableHours: entry.billable ? 0 : entry.time,
                    totalHours: entry.time,
                    hourlyRate: entry.hourlyRate,
                    monthlyRate: entry.monthlyRate,
                    billingAmount: !entry.billable ? 0 : (entry.hourlyRate > 0 ? entry.time / 60 * entry.hourlyRate : 0)
                }
                job.employees.push(employee);
            }
            else {
                employee.billableHours += entry.billable ? entry.time : 0;
                employee.nonBillableHours += entry.billable ? 0 : entry.time;
                employee.totalHours += entry.time;
                employee.billingAmount += !entry.billable ? 0 : (entry.hourlyRate > 0 ? (entry.time * entry.hourlyRate) / 60 : 0);
            }

            //Pie Data - updated for ApexCharts format
            const data = {
                labels: clients.map((c) => c.clientName),
                series: clients.map((c) => c.billingAmount),
                colors: PIECOLORS
            };

            setPieData(data);
        }

        return clients;
    }

    function JobRow(props: { jobRow: Job, index: number }) {
        const { jobRow, index } = props;
        const [open2, setOpen2] = React.useState(false);
        const [sortBy, setSortBy] = React.useState<'employeeName' | 'totalHours' | 'billableHours' | 'nonBillableHours' | 'billingAmount'>('billingAmount');
        const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

        const sortedEmployees = React.useMemo(() => {
            return [...jobRow.employees].sort((a, b) => {
                let aVal = a[sortBy];
                let bVal = b[sortBy];
                
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortOrder === 'asc' 
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }
                
                const aNum = Number(aVal);
                const bNum = Number(bVal);
                return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            });
        }, [jobRow.employees, sortBy, sortOrder]);

        return (<>
            <TableRow key={jobRow.idJob} className={(index % 2 == 0) ? "jobRowOdd" : "jobRow"} >
                <TableCell></TableCell>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen2(!open2)}>
                        {open2 ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                    {jobRow.jobDescription || '-'}
                </TableCell>
                <TableCell></TableCell>
                <TableCell align="right">{(jobRow.totalHours / 60).toFixed(2)}</TableCell>
                <TableCell align="right">{(jobRow.billableHours / 60).toFixed(2)}</TableCell>
                <TableCell align="right">{(jobRow.nonBillableHours / 60).toFixed(2)}</TableCell>
                <TableCell align="right">{formatCurrency(jobRow.billingAmount)}</TableCell>
            </TableRow>
            {open2 && sortedEmployees.map((employee, index) => (<EmployeeRow key={employee.idEmployee} employee={employee} index={index}></EmployeeRow>))
            }
        </>);

    }

    function ProjectRow(props: { row: Project, index: number }) {
        const { row, index } = props;
        const [open, setOpen] = React.useState(false);
        const [sortBy, setSortBy] = React.useState<'jobDescription' | 'totalHours' | 'billableHours' | 'nonBillableHours' | 'billingAmount'>('billingAmount');
        const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

        const handleSort = (field: typeof sortBy) => {
            const isAsc = sortBy === field && sortOrder === 'asc';
            setSortOrder(isAsc ? 'desc' : 'asc');
            setSortBy(field);
        };

        const sortedJobs = React.useMemo(() => {
            return [...row.jobs].sort((a, b) => {
                let aVal = a[sortBy];
                let bVal = b[sortBy];
                
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortOrder === 'asc' 
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }
                
                const aNum = Number(aVal);
                const bNum = Number(bVal);
                return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            });
        }, [row.jobs, sortBy, sortOrder]);

        return (
            <React.Fragment>
                <TableRow className={(index % 2 == 0) ? "projectRowOdd" : "projectRow"}>
                    <TableCell>
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                        <b>{row.projectName}</b>
                    </TableCell>
                    <TableCell scope="row" >
                    </TableCell>
                    <TableCell scope="row" >
                    </TableCell>
                    <TableCell scope="row" align="right">
                        {(row.totalHours / 60).toFixed(2)}
                    </TableCell>
                    <TableCell scope="row" align="right">
                        {(row.billableHours / 60).toFixed(2)}
                    </TableCell>
                    <TableCell scope="row" align="right">
                        {(row.nonBillableHours / 60).toFixed(2)}
                    </TableCell>
                    <TableCell scope="row" align="right">
                        {formatCurrency(row.billingAmount)}
                    </TableCell>
                </TableRow>

                {open &&
                    sortedJobs.map((jobRow, index) => (
                        <JobRow key={jobRow.idJob} jobRow={jobRow} index={index}></JobRow>
                    ))

                }

            </React.Fragment>
        );
    }

    function ClientRow(props: { client: Client }) {

        const { client } = props;
        const [sortBy, setSortBy] = React.useState<'projectName' | 'totalHours' | 'billableHours' | 'nonBillableHours' | 'billingAmount'>('billingAmount');
        const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

        const handleSort = (field: typeof sortBy) => {
            const isAsc = sortBy === field && sortOrder === 'asc';
            setSortOrder(isAsc ? 'desc' : 'asc');
            setSortBy(field);
        };

        const sortedProjects = React.useMemo(() => {
            return [...client.projects].sort((a, b) => {
                let aVal = a[sortBy];
                let bVal = b[sortBy];
                
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortOrder === 'asc' 
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }
                
                const aNum = Number(aVal);
                const bNum = Number(bVal);
                return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            });
        }, [client.projects, sortBy, sortOrder]);

        return (<Stack gap={2}>
            <Box display={'flex'} justifyContent='space-between' alignItems='center'>
                <Typography variant="h4" color='primary'>
                    {client.clientName}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary">
                    {formatCurrency(client.billingAmount)}
                </Typography>
            </Box>
            <TableContainer component={Paper} variant='outlined'>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortBy === 'projectName'}
                                    direction={sortBy === 'projectName' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('projectName')}
                                >
                                    <b>{intl.get('billingReportComponent.clientRow.tableCel.project')}</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell><b>{intl.get('billingReportComponent.clientRow.tableCel.job')}</b></TableCell>
                            <TableCell><b>{intl.get('billingReportComponent.clientRow.tableCel.employee')}</b></TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortBy === 'totalHours'}
                                    direction={sortBy === 'totalHours' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('totalHours')}
                                >
                                    <b>{intl.get('billingReportComponent.clientRow.tableCel.totalHours')}</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortBy === 'billableHours'}
                                    direction={sortBy === 'billableHours' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('billableHours')}
                                >
                                    <b>{intl.get('billingReportComponent.clientRow.tableCel.billable')}</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortBy === 'nonBillableHours'}
                                    direction={sortBy === 'nonBillableHours' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('nonBillableHours')}
                                >
                                    <b>{intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortBy === 'billingAmount'}
                                    direction={sortBy === 'billingAmount' ? sortOrder : 'asc'}
                                    onClick={() => handleSort('billingAmount')}
                                >
                                    <b>{intl.get('billingReportComponent.clientRow.tableCel.billingAmoun')}</b>
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedProjects.map((row, index) => (
                            <ProjectRow key={row.idProject} row={row} index={index} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>)
    }

    function EmployeeRow(props: { employee: Employee, index: number }) {
        const { employee, index } = props;
        return (<TableRow key={employee.idEmployee} className={(index % 2 == 0) ? "employeeRowOdd" : "employeeRow"} >
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell component="th" scope="row">
                {employee.employeeName}
                {employee.monthlyRate > 0 && <span> ({intl.get('billingReportComponent.monthly')})</span>}
            </TableCell>
            <TableCell align="right">
                {(employee.totalHours / 60).toFixed(2)}
            </TableCell>
            <TableCell align="right">
                {(employee.billableHours / 60).toFixed(2)}
            </TableCell>
            <TableCell align="right">
                {(employee.nonBillableHours / 60).toFixed(2)}
            </TableCell>
            <TableCell align="right">{formatCurrency(employee.billingAmount)}</TableCell>

        </TableRow>);
    }

    function EmployeeTotalsSection() {
        if (employeeTotals.length === 0) return null;
        
        const [sortBy, setSortBy] = React.useState<'employeeName' | 'totalHours' | 'billableHours' | 'nonBillableHours' | 'billingAmount'>('billingAmount');
        const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

        const handleSort = (field: typeof sortBy) => {
            const isAsc = sortBy === field && sortOrder === 'asc';
            setSortOrder(isAsc ? 'desc' : 'asc');
            setSortBy(field);
        };

        const sortedEmployees = React.useMemo(() => {
            return [...employeeTotals].sort((a, b) => {
                let aVal = a[sortBy];
                let bVal = b[sortBy];
                
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortOrder === 'asc' 
                        ? aVal.localeCompare(bVal)
                        : bVal.localeCompare(aVal);
                }
                
                const aNum = Number(aVal);
                const bNum = Number(bVal);
                return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
            });
        }, [employeeTotals, sortBy, sortOrder]);

        return (
            <Stack gap={2}>
                <Typography variant="h4" color='primary'>
                    {intl.get('billingReportComponent.teamMemberTotals.title')}
                </Typography>
                <TableContainer component={Paper} variant='outlined'>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortBy === 'employeeName'}
                                        direction={sortBy === 'employeeName' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('employeeName')}
                                    >
                                        <b>{intl.get('billingReportComponent.clientRow.tableCel.employee')}</b>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortBy === 'totalHours'}
                                        direction={sortBy === 'totalHours' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('totalHours')}
                                    >
                                        <b>{intl.get('billingReportComponent.clientRow.tableCel.totalHours')}</b>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortBy === 'billableHours'}
                                        direction={sortBy === 'billableHours' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('billableHours')}
                                    >
                                        <b>{intl.get('billingReportComponent.clientRow.tableCel.billable')}</b>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortBy === 'nonBillableHours'}
                                        direction={sortBy === 'nonBillableHours' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('nonBillableHours')}
                                    >
                                        <b>{intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}</b>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortBy === 'billingAmount'}
                                        direction={sortBy === 'billingAmount' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('billingAmount')}
                                    >
                                        <b>{intl.get('billingReportComponent.clientRow.tableCel.billingAmoun')}</b>
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedEmployees.map((employee, index) => (
                                <TableRow key={employee.idEmployee} className={(index % 2 == 0) ? "employeeRowOdd" : "employeeRow"}>
                                    <TableCell component="th" scope="row">
                                        {employee.employeeName}
                                        {employee.monthlyRate > 0 && <span> ({intl.get('billingReportComponent.monthly')})</span>}
                                    </TableCell>
                                    <TableCell align="right">
                                        {(employee.totalHours / 60).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {(employee.billableHours / 60).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {(employee.nonBillableHours / 60).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(employee.billingAmount)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        );
    }

    // Component to render the billing pie chart using ApexCharts
    const BillingPieChart = ({ data }) => {
        const chartOptions = useChart({
            //colors: PIECOLORS,
          colors:ACCESSIBLE_COLORS,
            labels: data.labels,
            legend: {
                show: true,
                position: 'bottom',
                horizontalAlign: 'center',
            },
            tooltip: {
                y: {
                    formatter: (value: number) => formatCurrency(value),
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '75%',
                    },
                },
            }
        });
        const formattedDataSeries = data.series.map((item) => Number(item.toFixed(2)));
        return (
            <Chart
                type="pie"
                series={formattedDataSeries}
                options={chartOptions}
                height={340}
                sx={{ p: 2.5 }}
            />
        );
    };

    return (<Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
        <Title>{intl.get('billingReportComponent.header')}</Title>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
            <AttachMoney /> {intl.get('billingReportComponent.header')}
        </Typography>
        <ReportsFilterComponent handleFilterChange={handleFilterChange} errors={errors} setErrors={setErrors}></ReportsFilterComponent>
        { loading ?
            <Box display='flex' justifyContent='center' alignItems='center'>
                <CircularProgress size={50}/>
            </Box>
        :
        <>
            {/* Total Billing Amount Card */}
            {clients.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3, mt: 2, bgcolor: 'primary.lighter' }}>
                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {intl.get('billingReportComponent.total.title')}
                            </Typography>
                            <Typography variant="h3" color="primary" fontWeight="bold">
                                {formatCurrency(clients.reduce((sum, client) => sum + client.billingAmount, 0))}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                                {intl.get('billingReportComponent.clientRow.tableCel.totalHours')}: {(clients.reduce((sum, client) => sum + client.totalHours, 0) / 60).toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.get('billingReportComponent.clientRow.tableCel.billable')}: {(clients.reduce((sum, client) => sum + client.billableHours, 0) / 60).toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}: {(clients.reduce((sum, client) => sum + client.nonBillableHours, 0) / 60).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                </Card>
            )}
            
            {pieData && pieData.series && pieData.series.some(x=>x>0) && 
                <BillingPieChart data={pieData} />
            }
            {pieData && pieData.series && !pieData.series.some(x=>x>0) && 
                <Typography display='flex' justifyContent='center' alignItems='center' gap={1}>
                    <Cancel/> {intl.get('billingReportComponent.data.noDataToDisplay')}
                </Typography>
            }
            <Stack gap={4}>
                <PermissionsGate viewScopes={[SCOPES['reports.billing.read']]} editScopes={[SCOPES['reports.billing.read']]} entityType={client && ENTITY_TYPE.CLIENT} viewRoles={[]} editRoles={[]} >
                    {sortedClients && sortedClients.map((client, i) =>
                        <ClientRow key={client.idClient} client={client}></ClientRow>
                    )}
                </PermissionsGate>
                
                {/* Team Member Totals Section */}
                <EmployeeTotalsSection />
                
                {!clients &&
                    <HoursByEmployeeReportComponent filterData={filterData} errors={errors} setErrors={setErrors}/>
                }
            </Stack>
        </>
        }
    </Box>
    </Card>)
}

export default BillingReportComponent;